import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";
import { GridBackground } from "./components/GridBackground";
import { FlowArrow } from "./components/FlowArrow";
import { DataParticle } from "./components/DataParticle";

const IDES = [
  { name: "Claude Desktop", icon: "🟣", y: 350 },
  { name: "Cursor", icon: "⚡", y: 500 },
  { name: "Windsurf", icon: "🌊", y: 650 },
];

export const Scene7MCP: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const titleAppear = spring({ frame, fps, config: SPRING.snappy });

  const exitOpacity = interpolate(frame, [100, 120], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill style={{ opacity: exitOpacity }}>
      <GridBackground opacity={0.06} />

      {/* Title */}
      <div
        style={{
          position: "absolute",
          top: 60,
          left: "50%",
          transform: "translate(-50%, 0)",
          textAlign: "center",
          opacity: interpolate(titleAppear, [0, 1], [0, 1]),
        }}
      >
        <div
          style={{
            fontSize: 42,
            fontFamily: FONTS.heading,
            fontWeight: 700,
            color: COLORS.textPrimary,
          }}
        >
          MCP{" "}
          <span style={{ color: COLORS.accentPurple }}>Integration</span>
        </div>
        <div
          style={{
            fontSize: 18,
            fontFamily: FONTS.body,
            color: COLORS.textSecondary,
            marginTop: 8,
          }}
        >
          Model Context Protocol — AI IDEs query your knowledge base
        </div>
      </div>

      {/* IDE boxes (left) */}
      {IDES.map((ide, i) => {
        const ideAppear = spring({
          frame: frame - 15 - i * 10,
          fps,
          config: SPRING.bouncy,
        });

        return (
          <React.Fragment key={i}>
            <div
              style={{
                position: "absolute",
                left: 200,
                top: ide.y,
                transform: `translate(-50%, -50%) scale(${interpolate(ideAppear, [0, 1], [0.5, 1])})`,
                opacity: interpolate(ideAppear, [0, 1], [0, 1]),
                width: 200,
                height: 70,
                borderRadius: 14,
                background: `${COLORS.bgCard}`,
                border: `1.5px solid ${COLORS.borderLight}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
              }}
            >
              <span style={{ fontSize: 28 }}>{ide.icon}</span>
              <span
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 15,
                  color: COLORS.textPrimary,
                  fontWeight: 500,
                }}
              >
                {ide.name}
              </span>
            </div>

            {/* Arrow from IDE to MCP Server */}
            <FlowArrow
              fromX={300}
              fromY={ide.y}
              toX={680}
              toY={500}
              delay={25 + i * 10}
              color={COLORS.accentPurple}
              strokeWidth={1.5}
              dashed
            />

            {/* Particles */}
            {frame > 35 + i * 10 && (
              <DataParticle
                fromX={305}
                fromY={ide.y}
                toX={675}
                toY={500}
                delay={35 + i * 10}
                duration={40}
                color={COLORS.accentPurple}
                size={4}
                count={2}
              />
            )}
          </React.Fragment>
        );
      })}

      {/* MCP Server (center) */}
      {(() => {
        const mcpAppear = spring({
          frame: frame - 30,
          fps,
          config: SPRING.bouncy,
        });

        return (
          <div
            style={{
              position: "absolute",
              left: 780,
              top: 500,
              transform: `translate(-50%, -50%) scale(${interpolate(mcpAppear, [0, 1], [0.3, 1])})`,
              opacity: interpolate(mcpAppear, [0, 1], [0, 1]),
              width: 220,
              height: 130,
              borderRadius: 20,
              background: `linear-gradient(135deg, ${COLORS.accentPurple}20, ${COLORS.accentBlue}10)`,
              border: `2px solid ${COLORS.accentPurple}50`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              boxShadow: `0 0 40px ${COLORS.accentPurple}15`,
            }}
          >
            <span style={{ fontSize: 32 }}>🔌</span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 18,
                fontWeight: 700,
                color: COLORS.textPrimary,
              }}
            >
              MCP Server
            </span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 12,
                color: COLORS.textMuted,
              }}
            >
              POST /api/mcp
            </span>
          </div>
        );
      })()}

      {/* Arrow from MCP to EAKC */}
      <FlowArrow
        fromX={890}
        fromY={500}
        toX={1180}
        toY={500}
        delay={50}
        color={COLORS.accentTeal}
        strokeWidth={2}
      />

      {frame > 55 && (
        <DataParticle
          fromX={895}
          fromY={500}
          toX={1175}
          toY={500}
          delay={55}
          duration={35}
          color={COLORS.accentTeal}
          size={5}
          count={3}
        />
      )}

      {/* EAKC Brain (right) */}
      {(() => {
        const brainAppear = spring({
          frame: frame - 45,
          fps,
          config: SPRING.bouncy,
        });

        const glowPulse = interpolate(
          Math.sin(frame * 0.05),
          [-1, 1],
          [30, 50],
        );

        return (
          <div
            style={{
              position: "absolute",
              left: 1300,
              top: 500,
              transform: `translate(-50%, -50%) scale(${interpolate(brainAppear, [0, 1], [0.3, 1])})`,
              opacity: interpolate(brainAppear, [0, 1], [0, 1]),
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
          >
            <div
              style={{
                width: 160,
                height: 160,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${COLORS.accentBlue}, ${COLORS.accentPurple})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 0 ${glowPulse}px ${COLORS.accentPurple}40, 0 0 ${glowPulse * 2}px ${COLORS.accentBlue}20`,
              }}
            >
              <span style={{ fontSize: 64 }}>🧠</span>
            </div>
            <span
              style={{
                fontFamily: FONTS.heading,
                fontSize: 22,
                fontWeight: 700,
                color: COLORS.textPrimary,
              }}
            >
              EAKC Knowledge Base
            </span>
            <span
              style={{
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.textMuted,
              }}
            >
              670+ documents · 1536-dim vectors
            </span>
          </div>
        );
      })()}

      {/* Tools list */}
      {(() => {
        const toolsAppear = spring({
          frame: frame - 70,
          fps,
          config: SPRING.snappy,
        });
        const tools = ["query_eakc()", "list_sources()", "get_document()"];
        return (
          <div
            style={{
              position: "absolute",
              right: 100,
              top: 300,
              opacity: interpolate(toolsAppear, [0, 1], [0, 1]),
            }}
          >
            <div
              style={{
                fontFamily: FONTS.mono,
                fontSize: 13,
                color: COLORS.textMuted,
                marginBottom: 8,
              }}
            >
              MCP Tools:
            </div>
            {tools.map((tool, i) => (
              <div
                key={i}
                style={{
                  fontFamily: FONTS.mono,
                  fontSize: 14,
                  color: COLORS.accentTeal,
                  padding: "4px 0",
                }}
              >
                → {tool}
              </div>
            ))}
          </div>
        );
      })()}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
